import { Button, ButtonText } from '@/components/ui/button'
import { Center } from '@/components/ui/center'
import { Text } from '@/components/ui/text'
import UniversalWebView from '@/components/UniversalWebView'
import { getAuthToken } from '@/lib/api/authToken'
import usePlatformColor from '@/lib/hooks/usePlatformColor'
import * as Sentry from '@sentry/react-native'
import { router } from 'expo-router'
import { useEffect, useState } from 'react'
import { Platform, StyleSheet, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { CHAT_CAPS, handleChatPurchase, type ChatPurchase } from '@/modules/handleChatPurchase'

const BRIDGE_MESSAGE_SOURCE = 'cs-chat-sdk'

const JIRI_WEB_BASE_URL = process.env.EXPO_PUBLIC_JIRI_WEB_BASE_URL

// Only an iframe has a bridge back to us: inside the App's WebView jiri-web posts `purchase` and nothing else,
// so declaring `openObject` there would leave its card buttons doing nothing.
const CAPS = Platform.OS === 'web' ? [CHAT_CAPS, 'openObject'].filter(Boolean).join(',') : CHAT_CAPS

type OpenObject = { kind?: 'form' | 'table' | 'query'; token?: string; url?: string }

type ConversationChanged = { conversationId?: string | null; reason?: 'opened' | 'created' | 'draft' }

type JiriBridgeMessage = { source?: string; type?: string; id?: string; data?: unknown }

function buildChatUrl(conversationId: string | undefined): string | null {
  if (!JIRI_WEB_BASE_URL) return null
  // The App's long-lived token, as the nextfe `/m/*` embeds carry it. jiri-web keeps `m_t` for the life of the
  // page and goldendata enforces a JWT's expiry, so the five-minute one from `jwt_oauth/new` signed a reader
  // with no main-site cookie (the App, a miniprogram web-view) out in the middle of a conversation.
  const token = getAuthToken()
  if (!token) return null

  const path = conversationId ? `/chat/${encodeURIComponent(conversationId)}` : '/chat'
  const chatUrl = new URL(path, JIRI_WEB_BASE_URL)
  chatUrl.searchParams.set('utm_device', 'mobile')
  chatUrl.searchParams.set('utm_source', 'gd_app')
  chatUrl.searchParams.set('utm_medium', 'nextapp')
  chatUrl.searchParams.set('m_t', token)
  if (CAPS) chatUrl.searchParams.set('caps', CAPS)
  return chatUrl.toString()
}

function openObject({ kind, token, url }: OpenObject) {
  if (kind === 'form' && token) {
    router.push(`/forms/${encodeURIComponent(token)}/edit`)
    return
  }
  if (kind === 'table' && token) {
    router.push(`/tables/${encodeURIComponent(token)}`)
    return
  }
  // A query has no screen here; its nextfe page is where jiri-web would have sent the reader itself. Opened
  // while the click in the frame still counts as the reader's, so the new tab is not blocked as a popup.
  if (url && /^https?:\/\//i.test(url)) window.open(url, '_blank', 'noopener')
}

function close() {
  if (router.canGoBack()) router.back()
  else router.replace('/forms')
}

/**
 * Jiri is jiri-web in a WebView on every platform, App and mobile web alike. eve is not stable yet and its
 * clients have to move with it; a store release cannot keep that pace, and jiri cannot serve every App
 * version still installed, so the chat stays on the side that deploys with jiri.
 */
export default function JiriWebView({ conversationId }: { conversationId?: string }) {
  // The page loads once: jiri-web moves between conversations inside it, and rebuilding the address from the
  // `conversationId` written back below would reload it under the reader.
  const [initialConversationId] = useState(conversationId)
  const [url, setUrl] = useState(() => buildChatUrl(initialConversationId))
  const [secondaryLabel] = usePlatformColor('secondaryLabel')
  const insets = useSafeAreaInsets()

  useEffect(() => {
    if (url) return
    // Rendering nothing here looked like a blank page. The usual cause is a signed-out session.
    Sentry.captureMessage('Jiri: no chat URL', { extra: { hasBaseUrl: !!JIRI_WEB_BASE_URL } })
  }, [url])

  if (!url) {
    return (
      <Center className="flex-1 px-8">
        <Text size="lg" className="font-semibold">
          Jiri 加载失败
        </Text>
        <Text size="md" style={{ color: secondaryLabel }} className="mt-2 text-center">
          请稍后重试；如果一直失败，请退出登录后重新登录
        </Text>
        <Button size="lg" className="mt-8 w-full max-w-sm" onPress={() => setUrl(buildChatUrl(initialConversationId))}>
          <ButtonText>重试</ButtonText>
        </Button>
      </Center>
    )
  }

  return (
    // env(safe-area-inset-bottom) reads 0 inside an iframe and the Android WebView, so the room is left here.
    <View style={[styles.container, Platform.OS !== 'ios' && { paddingBottom: insets.bottom }]}>
      <UniversalWebView<JiriBridgeMessage>
        source={{ uri: url }}
        style={styles.webview}
        allow="clipboard-write"
        onMessage={message => {
          // Must-ignore: a bridge type this build does not know is not an error.
          if (message?.source !== BRIDGE_MESSAGE_SOURCE) return
          switch (message.type) {
            case 'purchase': {
              const purchase = message.data as ChatPurchase | undefined
              if (purchase) handleChatPurchase(purchase)
              return
            }
            case 'openObject': {
              const object = message.data as OpenObject | undefined
              if (object) openObject(object)
              return
            }
            case 'close':
              close()
              return
            case 'conversationChanged': {
              // Keeps `/jiri?conversationId=` on the conversation in the frame, so a refresh reopens it.
              const changed = message.data as ConversationChanged | undefined
              router.setParams({ conversationId: changed?.conversationId ?? undefined })
              return
            }
          }
        }}
      />
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  webview: {
    flex: 1,
  },
})
